/**
 * Section / Shop The Look
 * -----------------------------------------------------------------------------
 * - Section for storing and passing the data to the Shop The Look Popover.
 * - Handles global click and keydown events to open/close the Popover
 *
 * @namespace ShopTheLookSection
 */

/**
 * Create a new renderless script object.
 * @param {HTMLElement} section - Scoped [data-mint-section] element.
 */
export default ({ section = false } = false) => {

  /**
   * Global state.
   */
  const shopTheLookPrefix = window.mint.shopTheLookPopover?.prefix || null

  /**
   * DOM selectors.
   */
  const selectors = {
    shopTheLookTrigger: `a[href^="#${shopTheLookPrefix}"]`,
  }

  /**
   * Node selectors.
   */
  const nodeSelectors = {
    anchorLinks: document.querySelectorAll(selectors.shopTheLookTrigger),
  }

  /**
   * Set event listeners for click and keydown events.
   */
  function setEventListeners() {
    document.addEventListener('click', handlePopoverOpen)
    document.addEventListener('keydown', handleKeydown)
  }

  /**
   * Listen for click and keydown events.
   * - Open the Shop The Look Popover if conditions match.
   *
   * @param {Event} event - The event object.
   *
   * @fires shopTheLookPopover:open
   */
  function handlePopoverOpen(event) {
    const isKeydown = event.type === 'keydown'

    if (isKeydown && event.key !== ' ' && event.key !== 'Enter') {
      return
    }

    const target = event.target.closest(selectors.shopTheLookTrigger)
    const namespace = target?.getAttribute('href')?.replace('#', '')
    const sectionsData = window.mint.shopTheLookPopover?.sectionsData || {}

    if (!target || !isValidShopTheLookTrigger(namespace)) {
      return
    }

    event.preventDefault()

    /**
     * Trigger the Shop The Look popover logic for the given namespace.
     */
    mint.EventBus.dispatch('shopTheLookPopover:open', {
      target,
      namespace,
      data: sectionsData[namespace],
    })
  }

  /**
   * Listen for keydown events.
   * - Open the Shop The Look Popover on `Space` or `Enter` press if corresponding
   * anchor link is focused.
   * - Close the Shop The Look Popover on `Escape` press.
   *
   * @param {Event} event - The event object.
   *
   * @fires shopTheLookPopover:open
   * @fires shopTheLookPopover:close
   */
  function handleKeydown(event) {
    if (!mint.accessibility.keyboardNavigation) {
      return
    }

    if (event.code.toUpperCase() === 'ESCAPE') {
      mint.EventBus.dispatch('shopTheLookPopover:close')
    } else {
      handlePopoverOpen(event)
    }
  }

  /**
   * Modify anchor links dynamically to include role="button" if valid triggers
   * exist.
   */
  function modifyAnchorLinks() {
    nodeSelectors.anchorLinks.forEach((link) => {
      const namespace = link.getAttribute('href').replace('#', '')

      if (isValidShopTheLookTrigger(namespace)) {
        link.setAttribute('role', 'button')
      }
    })
  }

  /**
   * Validate whether the namespace corresponds to a valid Shop The Look trigger.
   *
   * @param {string} namespace - The namespace extracted from the link `href`.
   * @returns {boolean}
   */
  function isValidShopTheLookTrigger(namespace) {
    if (!shopTheLookPrefix || !namespace) {
      return false
    }

    const sectionsData = window.mint.shopTheLookPopover?.sectionsData || {}
    const currentShopTheLookSection = sectionsData[namespace]

    return (
      currentShopTheLookSection &&
      currentShopTheLookSection.productHandles?.length
    )
  }

  /**
   * Initialise section.
   */
  function init() {
    if (!shopTheLookPrefix || !nodeSelectors.anchorLinks.length) {
      return
    }

    modifyAnchorLinks()
    setEventListeners()
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
